import {
  GetSingleInspectionInfoQuery,
  GetSingleInspectionInfoQueryVariables,
  InspectionResult,
} from '@/api/ariadne/__generated_types__/apollo';
import { GET_SINGLE_INSPECTION_INFO } from '@/api/ariadne/queries/get-single-inspection-info';
import { useQuery } from '@apollo/client';
import { ArrowLeftLine } from '@rsuite/icons';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Col, Grid, IconButton, Row, Toggle } from 'rsuite';
import styled from 'styled-components';
import {
  InspectionQuestionSection,
  Maybe,
} from '../../api/ariadne/__generated_types__/graphql';
import { BouncingBoxWithText } from '../../widgets/bouncing-box-loader';
import { CleanModal } from '../../widgets/components/clean-modal';
import { H3 } from '../../widgets/components/title-styles';
import { INSPECTION_BOOKED, InspectionText } from '../constants';
import { InspectionSection } from './inspection-section';
import { useApnData } from './useApnData';

const Header = styled.div`
  display: block;
  margin-bottom: 24px;

  @media print {
    display: none;
  }
`;

const InspectionDetailsContent = styled.div`
  margin: 23px 36px 24px;

  @media print {
    display: none;
  }
`;

const DetailRow = styled(Row)`
  margin: 24px 0 18px;
`;

const DetailsLabel = styled.span`
  font-size: 12px;
  font-weight: bold;
`;

const DetailsContent = styled.span`
  font-size: 12px;
`;

const ToggleContainer = styled.div`
  display: flex;
  justify-content: end;
  width: 100%;
`;

const ContentRow = styled(Row)`
  height: 70vh;
`;

interface InspectionDetailsProps {
  regionId: string;
  inspectionId: string;
  onClose: () => void;
  configuration?: Array<Maybe<InspectionQuestionSection>>;
}

export const InspectionDetails = ({
  inspectionId,
  onClose,
  regionId,
  configuration,
}: InspectionDetailsProps) => {
  const [isPassShown, setIsPassShow] = useState(false);

  const { data, loading } = useQuery<
    GetSingleInspectionInfoQuery,
    GetSingleInspectionInfoQueryVariables
  >(GET_SINGLE_INSPECTION_INFO, {
    variables: { regionId, inspectionId },
  });

  const inspectionFields = data?.getSingleInspectionInfo?.basicInspectionFields;

  const inspectionStatus = inspectionFields?.inspectionResult
    ? InspectionText[inspectionFields?.inspectionResult as InspectionResult]
    : 'Not started';

  const results = inspectionFields?.misc.inspectionResultBreakdown;

  const onChangePasses = (checked: boolean): void => {
    setIsPassShow(checked);
  };

  const apnDataResponse = useApnData(regionId, inspectionFields?.apn);

  const apnData =
    apnDataResponse?.data?.getParcelDataAndPropertyContactsByApn &&
    'parcelData' in apnDataResponse.data.getParcelDataAndPropertyContactsByApn
      ? apnDataResponse.data.getParcelDataAndPropertyContactsByApn
      : undefined;

  return (
    <CleanModal active>
      <InspectionDetailsContent>
        <Header>
          <IconButton
            size="sm"
            data-cy="inspections-back-button"
            icon={<ArrowLeftLine />}
            placement="left"
            onClick={onClose}
          >
            Back
          </IconButton>
        </Header>
        <>
          <Grid fluid>
            <Row>
              <Col xs={20}>
                <H3>Inspection Detail</H3>
              </Col>
              <Col xs={4}>
                <ToggleContainer>
                  <Toggle
                    size="md"
                    checkedChildren="Hide Passes"
                    unCheckedChildren="Show passes"
                    checked={isPassShown}
                    onChange={onChangePasses}
                  />
                </ToggleContainer>
              </Col>
            </Row>
          </Grid>
          <Grid fluid>
            <DetailRow>
              <Col xs={12}>
                <Grid fluid>
                  <Row>
                    <Col xs={8}>
                      <DetailsLabel>Property</DetailsLabel>
                    </Col>
                    <Col xs={16}>
                      <DetailsContent>
                        {inspectionFields?.propertyAddress}
                      </DetailsContent>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={8}>
                      <DetailsLabel>Result</DetailsLabel>
                    </Col>
                    <Col xs={16}>
                      <DetailsContent>{inspectionStatus}</DetailsContent>
                    </Col>
                  </Row>
                </Grid>
              </Col>
              <Col xs={12}>
                <Grid fluid>
                  <Row>
                    <Col xs={8}>
                      <DetailsLabel>Inspector</DetailsLabel>
                    </Col>
                    <Col xs={16}>
                      <DetailsContent>
                        {inspectionFields?.inspector || INSPECTION_BOOKED}
                      </DetailsContent>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={8}>
                      <DetailsLabel>Inspection Date</DetailsLabel>
                    </Col>
                    <Col xs={16}>
                      <DetailsContent>
                        {dayjs(inspectionFields?.inspectionTime).format(
                          'YYYY-MM-DD HH:mm'
                        )}
                      </DetailsContent>
                    </Col>
                  </Row>
                </Grid>
              </Col>
            </DetailRow>
            <ContentRow gutter={36}>
              {!loading &&
                configuration?.map((section, index) => {
                  const type = section?.type as string;
                  const res = results ? results[type] || null : null;
                  return (
                    <Col xs={12} key={index}>
                      {section && (
                        <InspectionSection
                          configQuestion={section}
                          results={res}
                          showPass={isPassShown}
                          inspectionResult={
                            inspectionFields?.inspectionResult as InspectionResult
                          }
                          apnData={apnData}
                        />
                      )}
                    </Col>
                  );
                })}
              {loading && (
                <BouncingBoxWithText loadingText="Loading Inspection Details" />
              )}
            </ContentRow>
          </Grid>
        </>
      </InspectionDetailsContent>
    </CleanModal>
  );
};
